import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/authApi'

// Async thunks
export const loginTeacher = createAsyncThunk(
  'auth/loginTeacher',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { email, password })
      const { token, user } = response.data
      
      // Validate user role - only allow teaching staff roles
      const allowedRoles = ['mentor', 'batch manager', 'doubt solver']
      const userRole = user.role?.toLowerCase() || ''
      
      if (!allowedRoles.includes(userRole)) {
        return rejectWithValue(
          `Access denied. This portal is only for teaching staff. Your role: ${user.role || 'Unknown'}`
        )
      }
      
      // Store token in localStorage
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      
      return { token, user }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Login failed'
      )
    }
  }
)

export const logoutTeacher = createAsyncThunk(
  'auth/logoutTeacher',
  async (_, { rejectWithValue }) => {
    try {
      // Clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      return true
    } catch (error) {
      return rejectWithValue('Logout failed')
    }
  }
)

export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      
      if (token && userStr) {
        const user = JSON.parse(userStr)
        
        // Validate user role - only allow teaching staff roles
        const allowedRoles = ['mentor', 'batch manager', 'doubt solver']
        const userRole = user.role?.toLowerCase() || ''
        
        if (!allowedRoles.includes(userRole)) {
          // Clear invalid user from localStorage
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          return null
        }
        
        return { token, user }
      }
      
      return null
    } catch (error) {
      return rejectWithValue('Failed to initialize auth')
    }
  }
)

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setAuth: (state, action) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginTeacher.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginTeacher.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(loginTeacher.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
        state.isAuthenticated = false
      })
      
      // Logout
      .addCase(logoutTeacher.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null
      })
      
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false
        state.isInitialized = true
        
        if (action.payload) {
          state.user = action.payload.user
          state.token = action.payload.token
          state.isAuthenticated = true
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false
        state.isInitialized = true
        state.isAuthenticated = false
      })
  },
})

export const { clearError, setAuth, clearAuth } = authSlice.actions
export default authSlice.reducer